import "./App.css";
import CssBaseline from "@mui/material/CssBaseline";
import React from "react";
import { AnimatePresence } from "motion/react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { DesktopLayout } from "@ntg/component-library/app";
import { LayoutContextProvider, UserContextProvider, useValidate } from "@ntg/component-library/contexts";
import { ThemeProvider } from "@mui/material/styles";
import { createCSDarkTheme, createCSTheme } from "@ntg/component-library/theming";
import { formatHex, oklch, filterBrightness, Oklch } from "culori";
import Allocations from "./sites/Allocations";
import Blank from "./Blank";
import Capabilities from "./sites/Capabilities";
import Capacities from "./sites/Capacities";
import Hours from "./sails/Hours";
import MaterialPredictions from "./sails/MaterialPredictions";
import Processes from "./sails/Processes";
import Transit from "./sites/Transit";
import { DrawerContents } from "./DrawerContents";
/**
 * Ensures a minimum lightness for a given OKLCH color.
 * If the color is too dark, it adjusts only the lightness component.
 *
 * @param color The input color in OKLCH
 * @param minL Minimum lightness (0 to 1)
 * @returns Adjusted color with enforced lightness
 */
function ensureMinimumLightness(color: Oklch, minL: number): Oklch {
    return {
        ...color,
        l: Math.max(color.l, minL),
    };
}

export default function App() {
    const { isLoading, error } = useValidate();
    // Copilot Comment: Using base DrawerContents directly instead of badge-wrapped version

    if (isLoading) return "Authenticating...";

    if (error) return "An error has occurred: " + error.message;

    const isDarkMode: boolean = true;
    const menuBar = "#720872ff";

    const base = oklch(menuBar);
    if (!base) throw new Error("Invalid base color");

    const shift = (lShift: number, cShift: number, hShift: number) => ({
        mode: "oklch" as const,
        l: Math.max(0, Math.min(1, base.l + lShift)),
        c: Math.max(0, base.c + cShift),
        h: ((base.h ?? 0) + hShift + 360) % 360,
    });

    const background = formatHex(shift(isDarkMode ? -0.8 : 0.1, -0.09, 0));
    const secondaryMenuBar = formatHex(shift(isDarkMode ? -0.3 : 0.3, -0.05, -50));

    const rawPrimary = shift(0.1, 0.05, 0); // saturated version of menuBar
    const rawSecondary = shift(0.1, 0.05, 30); // hue-shifted variant

    const primary = formatHex(ensureMinimumLightness(rawPrimary, 0.55)); // enforce brightness
    const secondary = formatHex(ensureMinimumLightness(rawSecondary, 0.55));

    const tertiary = "#044714ff";
    const quaternary = "#411570ff";

    const indicator = formatHex(shift(0.8, 0, 180));

    let theme;
    const brighten = filterBrightness(0.2, "oklch");
    if (isDarkMode) {
        theme = createCSDarkTheme({
            background: background,
            backgroundPaper: formatHex(brighten(background)) ?? background,
            menuBar: menuBar,
            secondaryMenuBar: secondaryMenuBar,
            indicator: indicator,
            primary: primary,
            secondary: secondary,
            tertiary: tertiary,
            quaternary: quaternary,
        });
    } else {
        theme = createCSTheme({
            background: background,
            backgroundPaper: formatHex(brighten(background)) ?? background,
            menuBar: menuBar,
            secondaryMenuBar: secondaryMenuBar,

            indicator: indicator,
            primary: primary,
            secondary: secondary,
            tertiary: tertiary,
            quaternary: quaternary,
        });
    }

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <UserContextProvider>
                <BrowserRouter>
                    <LayoutContextProvider
                        DrawerContents={DrawerContents}
                        ModuleName={"PlanningAdmin"}
                        ModuleTitle={"Planning Admin"}
                    >
                        <DesktopLayout
                            MainContent={
                                <AnimatePresence mode="wait">
                                    <Routes>
                                        <Route element={<Blank />} path="/" />
                                        <Route element={<Blank />} path="/home" />
                                        <Route element={<Blank />} path="/mastersitelist" />
                                        <Route element={<Transit />} path="/transit" />
                                        <Route element={<Processes />} path="/processes" />
                                        <Route element={<Capabilities />} path="/sitecapabilities" />
                                        <Route element={<Allocations />} path="/siteallocations" />
                                        <Route element={<Capacities />} path="/sitecapacities" />
                                        <Route element={<Hours />} path="/hours" />
                                        <Route element={<MaterialPredictions />} path="/materialpredictions" />
                                    </Routes>
                                </AnimatePresence>
                            }
                            TerminalContent={<div></div>}
                        />
                    </LayoutContextProvider>
                </BrowserRouter>
            </UserContextProvider>
        </ThemeProvider>
    );
}
